// W19 — Új projekt varázsló · W21 — Ismétlődés · W22 — Sablonszerkesztő.

import { t } from '../lib/i18n';
import { wizardStepper } from '../lib/components';
import { openDialog } from '../lib/dialog';
import { h, icon, icons } from '../lib/dom';
import { numberField, selectField, textField, validateAll, type FieldControl } from '../lib/fields';
import { priorityLabel, priorityMeta, type TaskPriority } from '../lib/model';
import {
  describe,
  toRRule,
  nextOccurrences,
  validate as validateRecurrence,
  weekdayName,
  weekdayOrder,
  type Frequency,
  type Recurrence,
} from '../lib/rrule';

// ---------------------------------------------------------------------------
// Közös: sorrendezhető feladatlista (W19 3. lépés és W22)
// ---------------------------------------------------------------------------

export type TemplateTask = {
  title: string;
  jobTitle?: string;
  estimateMinutes?: number;
  priority: TaskPriority;
};

/// Sorrendezhető lista.
///
/// Húzás helyett (vagy mellett) fel/le gombokkal is át lehet rendezni: a
/// húzás billentyűzettel nem elérhető, és az átadási leírás szerint minden
/// műveletnek elérhetőnek kell lennie Tabbal is.
/// A sablonfeladat alcíme: munkakör és becsült idő.
function templateTaskMeta(task: TemplateTask): string {
  const parts = [
    task.jobTitle ? t('task.jobTitlePrefix', [task.jobTitle]) : null,
    task.estimateMinutes ? t('task.estimatePrefix', [formatEstimate(task.estimateMinutes)]) : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : t('task.noJobTitleGiven');
}

/// A becslés emberi alakban: a terv „1 óra" / „30 perc" formában írja ki,
/// nem nyers percben.
function formatEstimate(minutes: number): string {
  if (minutes < 60) return t('task.minutesShort', [String(minutes)]);
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0
    ? t('task.hoursShort', [String(hours)])
    : t('task.hoursMinutesShort', [String(hours), String(rest)]);
}

/// Átrendezhető feladatsor a terv szerint: egyetlen panel, 1 px-es
/// elválasztókkal, nem különálló kártyák.
///
/// A tervben húzható fogantyú van; itt fel/le nyilak maradtak, mert azok
/// billentyűzettel és képernyőolvasóval is működnek. A fogantyú ikonja
/// megmaradt jelzésként, hogy a sor átrendezhető.
function reorderableList(
  tasks: TemplateTask[],
  onChange: () => void,
  onEdit?: (index: number) => void,
): HTMLElement {
  const host = h('div', { class: 'panel', style: { overflow: 'hidden' } });

  function render(): void {
    host.replaceChildren(
      ...tasks.map((task, index) =>
        h(
          'div',
          {
            class: 'row g10',
            style: {
              alignItems: 'center',
              padding: '10px 12px',
              borderBottom: '1px solid var(--border)',
            },
          },
          h(
            'span',
            { class: 'ic', style: { color: 'var(--tm)', flex: 'none' } },
            icon(icons.drag),
          ),
          h('span', {
            class: 'mono caption',
            style: { width: '26px', flex: 'none' },
            text: `${index + 1}.`,
          }),
          h(
            'span',
            { class: 'col grow', style: { gap: '0', minWidth: '0' } },
            h('span', { class: 'body-sm w6', text: task.title }),
            h('span', { class: 'caption', text: templateTaskMeta(task) }),
          ),
          h('span', {
            class: `prio ${priorityMeta[task.priority].cls}`,
            style: { height: '20px', fontSize: '11px', flex: 'none' },
            text: priorityLabel(task.priority),
          }),
          h('button', {
            class: 'btn btn-ghost btn-icon btn-sm',
            type: 'button',
            'aria-label': `${task.title} feljebb`,
            text: '↑',
            disabled: index === 0 ? 'disabled' : undefined,
            onClick: () => {
              const previous = tasks[index - 1]!;
              tasks[index - 1] = task;
              tasks[index] = previous;
              render();
              onChange();
            },
          }),
          h('button', {
            class: 'btn btn-ghost btn-icon btn-sm',
            type: 'button',
            'aria-label': `${task.title} lejjebb`,
            text: '↓',
            disabled: index === tasks.length - 1 ? 'disabled' : undefined,
            onClick: () => {
              const nextTask = tasks[index + 1]!;
              tasks[index + 1] = task;
              tasks[index] = nextTask;
              render();
              onChange();
            },
          }),
          onEdit
            ? h(
                'button',
                {
                  class: 'btn btn-ghost btn-icon btn-sm',
                  type: 'button',
                  'aria-label': t('task.editNamed', [task.title]),
                  onClick: () => onEdit(index),
                },
                h('span', { class: 'ic', style: { width: '15px', height: '15px' } }, icon(icons.pencil, 15)),
              )
            : null,
          h('button', {
            class: 'btn btn-ghost btn-icon btn-sm',
            type: 'button',
            'aria-label': t('task.deleteNamed2', [task.title]),
            text: '✕',
            onClick: () => {
              tasks.splice(index, 1);
              render();
              onChange();
            },
          }),
        ),
      ),
    );
  }

  render();
  return host;
}

// ---------------------------------------------------------------------------
// W19 — Új projekt varázsló
// ---------------------------------------------------------------------------

export type ProjectTemplate = {
  id: string;
  name: string;
  tasks: TemplateTask[];
};

export type NewProjectInput = {
  title: string;
  description: string;
  templateId?: string;
  tasks: TemplateTask[];
};

/// FÜGGVÉNY, nem konstans: modul szintű tömbként a fájl betöltésekor állna
/// össze, a szótár viszont csak a render előtt érkezik meg.
function wizardSteps(): string[] {
  return [
    t('task.wizardStepTemplate'),
    t('task.dataTab'),
    t('task.stepTaskOrder'),
    t('task.fieldPreview'),
  ];
}

/// Egy sablonfeladat szerkesztése (W19 „Szerkesztés" és „Feladat hozzáadása").
///
/// Eddig a varázslóban a sablonból hozott feladatokat csak átrendezni és
/// törölni lehetett — se újat felvenni, se a munkakörét vagy a becslését
/// módosítani. A terv mindkettőt mutatja.
export function openTemplateTaskDialog(options: {
  task?: TemplateTask;
  jobTitles: string[];
  onSubmit: (task: TemplateTask) => void;
}): void {
  const title = textField({
    label: t('task.projectTitle'),
    required: true,
    maxLength: 120,
    value: options.task?.title,
  });

  const jobTitle = selectField({
    label: t('people.jobTitle'),
    value: options.task?.jobTitle ?? '',
    choices: [
      { value: '', label: 'Nincs megadva' },
      ...options.jobTitles.map((name) => ({ value: name, label: name })),
    ],
  });

  const estimate = numberField({
    label: t('task.estimateMinutes'),
    min: 0,
    mono: true,
  });
  if (options.task?.estimateMinutes !== undefined) {
    estimate.input.value = String(options.task.estimateMinutes);
  }

  const priority = selectField({
    label: t('task.priority'),
    required: true,
    value: options.task?.priority ?? 'mid',
    choices: (Object.keys(priorityMeta) as TaskPriority[]).map((p) => ({
      value: p,
      label: priorityLabel(p),
    })),
  });

  openDialog({
    title: options.task ? t('general.editTask') : t('task.addTaskToTemplate'),
    width: '520px',
    body: () =>
      h(
        'div',
        { class: 'col g16' },
        title.element,
        h('div', { class: 'grid c2' }, jobTitle.element, estimate.element),
        priority.element,
      ),
    footer: (close) => [
      h('span', { class: 'grow' }),
      h('button', { class: 'btn btn-secondary', type: 'button', text: t('general.cancel'), onClick: close }),
      h('button', {
        class: 'btn btn-primary',
        type: 'button',
        text: t('general.save'),
        onClick: () => {
          if (!validateAll([title, priority] as FieldControl<HTMLElement>[])) return;
          const minutes = Number(estimate.input.value);
          options.onSubmit({
            title: title.input.value.trim(),
            priority: priority.input.value as TaskPriority,
            jobTitle: jobTitle.input.value || undefined,
            estimateMinutes: Number.isFinite(minutes) && minutes > 0 ? minutes : undefined,
          });
          close();
        },
      }),
    ],
  });
}

export function openNewProjectWizard(options: {
  templates: ProjectTemplate[];
  /// A cég munkakörei — a sablonfeladat szerkesztőjének választéka.
  jobTitles?: string[];
  onSubmit: (project: NewProjectInput) => void;
}): void {
  const steps = wizardSteps();
  let step = 0;
  let templateId: string | undefined;
  let tasks: TemplateTask[] = [];

  const title = textField({ label: t('task.projectTitleLabel'), required: true, maxLength: 120 });

  const description = textField({ label: t('general.description'), multiline: true, maxLength: 2000 });

  const body = h('div', { class: 'col g16' });
  const stepper = h('div');
  const footer = h('div', { class: 'row g8', style: { alignItems: 'center', width: '100%' } });

  function selectedTemplate(): ProjectTemplate | undefined {
    return options.templates.find((t) => t.id === templateId);
  }

  /// A terv jobb oldali, 300 px-es oszlopa: a kiválasztott sablon kártyája és
  /// az eddig megadott adatok. Enélkül a harmadik lépésen már nem látszott,
  /// milyen projektbe kerülnek ezek a feladatok.
  /// A jobb oldali oszlop allando hoszja: gepeles kozben is frissitheto,
  /// anelkul hogy az egesz lepes ujraepulne (az kidobna a fokuszt a mezobol).
  const sideHost = h('div', { class: 'col g12', style: { width: '300px', flex: 'none' } });

  function sidePanel(): HTMLElement {
    sideHost.replaceChildren(...sideContent());
    return sideHost;
  }

  function sideContent(): HTMLElement[] {
    const template = selectedTemplate();

    const rows: [string, string][] = [
      [t('task.projectName'), title.input.value.trim() || '—'],
      [t('task.wizardStepTemplate'), template ? template.name : t('task.emptyProject')],
      [t('nav.tasks'), t('task.taskCountShort', [String(tasks.length)])],
    ];
    if (description.input.value.trim()) {
      rows.push([t('general.description'), description.input.value.trim()]);
    }

    return [
      h(
        'div',
        {
          class: 'card pad16 col',
          style: {
            gap: '6px',
            borderColor: template ? 'var(--accent)' : 'var(--border)',
            background: template ? 'var(--accent-soft)' : undefined,
          },
        },
        h(
          'span',
          {
            class: 'ic',
            style: {
              width: '30px',
              height: '30px',
              borderRadius: '8px',
              background: 'var(--sunken)',
              color: 'var(--ts)',
            },
          },
          icon(icons.folder, 16),
        ),
        h('span', { class: 'body w6', text: template ? template.name : t('task.emptyProject') }),
        h('span', {
          class: 'caption',
          text: template ? t('task.selectedTemplate') : t('task.startsWithoutTemplate'),
        }),
      ),
      h(
        'div',
        { class: 'card pad16 col', style: { gap: '2px' } },
        ...rows.map(([label, value]) =>
          h(
            'div',
            {
              class: 'row g12',
              style: {
                alignItems: 'baseline',
                padding: '7px 0',
                borderBottom: '1px solid var(--border)',
              },
            },
            h('span', { class: 'caption', style: { width: '110px', flex: 'none' }, text: label }),
            h('span', {
              class: 'body-sm',
              style: { flexGrow: '1', minWidth: '0', overflowWrap: 'anywhere' },
              text: value,
            }),
          ),
        ),
      ),
    ];
  }

  function renderBody(): void {
    stepper.replaceChildren(wizardStepper(steps, step));
    // A lablec a feladatszamot is kiirja, ezert minden ujrarajzolas utan
    // frissiteni kell — kulonben egy uj feladat felvetele utan is „0 feladat
    // jon letre" allna ott.
    renderFooter();

    if (step === 0) {
      body.replaceChildren(
        h(
          'div',
          { class: 'col', style: { gap: '2px' } },
          h('span', { class: 'h3', text: t('task.wizardStepTemplate') }),
          h('span', {
            class: 'caption',
            text: t('task.chooseTemplateHint'),
          }),
        ),
        h(
          'div',
          { class: 'grid c3' },
          templateCard(
            t('task.emptyProject'),
            t('task.startsWithoutTasks'),
            templateId === undefined,
            () => {
              templateId = undefined;
              tasks = [];
              renderBody();
            },
          ),
          ...options.templates.map((template) =>
            templateCard(
              template.name,
              `${template.tasks.length} feladat`,
              templateId === template.id,
              () => {
                templateId = template.id;
                // Masolat, hogy a sablon ne valtozzon a varazsloban.
                tasks = template.tasks.map((t) => ({ ...t }));
                renderBody();
              },
            ),
          ),
        ),
      );
      return;
    }

    if (step === 1) {
      body.replaceChildren(
        h(
          'div',
          { class: 'row g20', style: { alignItems: 'flex-start', gap: '20px' } },
          h(
            'div',
            { class: 'panel pad20 col g14', style: { flexGrow: '1', minWidth: '0' } },
            h(
              'div',
              { class: 'col', style: { gap: '2px' } },
              h('span', { class: 'h3', text: 'Adatok' }),
              h('span', { class: 'caption', text: t('task.titleUsedEverywhere') }),
            ),
            title.element,
            description.element,
          ),
          sidePanel(),
        ),
      );
      return;
    }

    if (step === 2) {
      body.replaceChildren(
        h(
          'div',
          { class: 'row', style: { alignItems: 'flex-start', gap: '20px' } },
          h(
            'div',
            { class: 'panel pad20 col g14', style: { flexGrow: '1', minWidth: '0' } },
            h(
              'div',
              { class: 'col', style: { gap: '2px' } },
              h('span', { class: 'h3', text: 'Feladatsorrend' }),
              h('span', {
                class: 'caption',
                text:
                  t('task.reorderHint'),
              }),
            ),
            tasks.length === 0
              ? h('span', { class: 'body-sm tm', text: t('task.noTaskInProject') })
              : reorderableList(tasks, () => {
                  renderBody();
                }, (index) => {
                  openTemplateTaskDialog({
                    task: tasks[index],
                    jobTitles: options.jobTitles ?? [],
                    onSubmit: (updated) => {
                      tasks[index] = updated;
                      renderBody();
                    },
                  });
                }),
            h(
              'button',
              {
                class: 'btn btn-secondary',
                type: 'button',
                style: { alignSelf: 'flex-start' },
                onClick: () =>
                  openTemplateTaskDialog({
                    jobTitles: options.jobTitles ?? [],
                    onSubmit: (created) => {
                      tasks.push(created);
                      renderBody();
                    },
                  }),
              },
              h('span', { class: 'ic', style: { width: '14px', height: '14px' } }, icon(icons.plus, 14)),
              h('span', { text: t('task.addTaskToTemplate') }),
            ),
          ),
          sidePanel(),
        ),
      );
      return;
    }

    body.replaceChildren(
      h(
        'div',
        { class: 'row', style: { alignItems: 'flex-start', gap: '20px' } },
        h(
          'div',
          { class: 'panel pad20 col g14', style: { flexGrow: '1', minWidth: '0' } },
          h(
            'div',
            { class: 'col', style: { gap: '2px' } },
            h('span', { class: 'h3', text: title.input.value.trim() || t('task.untitled') }),
            h('span', { class: 'caption', text: t('task.tasksWillBeCreated', [String(tasks.length)]) }),
          ),
          description.input.value.trim()
            ? h('span', { class: 'body-sm ts', text: description.input.value.trim() })
            : null,
          h(
            'div',
            { class: 'panel', style: { overflow: 'hidden' } },
            ...tasks.map((task, index) =>
              h(
                'div',
                {
                  class: 'row g10',
                  style: {
                    alignItems: 'center',
                    padding: '10px 12px',
                    borderBottom: '1px solid var(--border)',
                  },
                },
                h('span', {
                  class: 'mono caption',
                  style: { width: '26px', flex: 'none' },
                  text: `${index + 1}.`,
                }),
                h(
                  'span',
                  { class: 'col grow', style: { gap: '0', minWidth: '0' } },
                  h('span', { class: 'body-sm w6', text: task.title }),
                  h('span', { class: 'caption', text: templateTaskMeta(task) }),
                ),
                h('span', {
                  class: `prio ${priorityMeta[task.priority].cls}`,
                  style: { height: '20px', fontSize: '11px' },
                  text: priorityLabel(task.priority),
                }),
              ),
            ),
          ),
        ),
        sidePanel(),
      ),
    );
  }

  function renderFooter(): void {
    const last = step === steps.length - 1;
    footer.replaceChildren(
      h('button', {
        class: 'btn btn-secondary',
        type: 'button',
        text: step === 0 ? t('general.cancel') : t('general.back'),
        onClick: () => {
          if (step === 0) {
            handle.close();
            return;
          }
          step -= 1;
          renderBody();
        },
      }),
      // A terv a lepesszam mellett azt is kiirja, hany feladat jon letre.
      h('span', {
        class: 'caption',
        style: { marginLeft: 'auto', marginRight: 'auto' },
        text: t('task.wizardFooter', [
          String(step + 1),
          String(steps.length),
          String(tasks.length),
        ]),
      }),
      h('button', {
        class: 'btn btn-primary',
        type: 'button',
        text: last ? t('ai.createProject') : t('task.nextStepShort', [steps[step + 1] ?? '']),
        onClick: () => {
          // A 2. lepesrol csak kitoltott cimmel lehet tovabblepni.
          if (step === 1 && !validateAll([title] as FieldControl<HTMLElement>[])) return;
          if (!last) {
            step += 1;
            renderBody();
            return;
          }
          options.onSubmit({
            title: title.input.value.trim(),
            description: description.input.value.trim(),
            templateId,
            tasks,
          });
          handle.close();
        },
      }),
    );
  }

  const handle = openDialog({
    title: t('task.newProjectTitle'),
    // A terv 980 px-es oszlopban mutatja a varazslot.
    width: '980px',
    body: () => h('div', { class: 'col g16' }, stepper, body),
    footer: () => [footer],
  });

  title.input.addEventListener('input', () => {
    // A cim a jobb oldali osszefoglaloban is szerepel; csak azt rajzoljuk
    // ujra, hogy a fokusz a mezoben maradjon.
    sideHost.replaceChildren(...sideContent());
  });

  renderBody();
}

/// A sablonválasztó kártyája a terv szerint: ikoncsempe, név, alcím.
function templateCard(
  name: string,
  caption: string,
  selected: boolean,
  onSelect: () => void,
): HTMLElement {
  return h(
    'button',
    {
      type: 'button',
      class: 'card pad16 col',
      'aria-pressed': selected ? 'true' : 'false',
      style: {
        gap: '6px',
        textAlign: 'left',
        font: 'inherit',
        cursor: 'pointer',
        borderColor: selected ? 'var(--accent)' : 'var(--border)',
        background: selected ? 'var(--accent-soft)' : undefined,
      },
      onClick: onSelect,
    },
    h(
      'span',
      {
        class: 'ic',
        style: {
          width: '30px',
          height: '30px',
          borderRadius: '8px',
          background: 'var(--sunken)',
          color: 'var(--ts)',
        },
      },
      icon(icons.folder, 16),
    ),
    h('span', { class: 'body w6', text: name }),
    h('span', { class: 'caption', text: selected ? t('task.selectedTemplate') : caption }),
  );
}

// ---------------------------------------------------------------------------
// W21 — Ismétlődés
// ---------------------------------------------------------------------------

export function openRecurrenceDialog(options: {
  projectTitle: string;
  initial?: Recurrence;
  onSubmit: (result: { recurrence: Recurrence; rrule: string }) => void;
}): void {
  const recurrence: Recurrence = options.initial ?? {
    frequency: 'monthly',
    interval: 1,
    monthly: { kind: 'dayOfMonth', day: 1 },
    end: { kind: 'never' },
  };

  const body = h('div', { class: 'col g16' });
  const summary = h('p', { class: 'body' });
  const rruleText = h('code', { class: 'mono caption' });
  const problems = h('div', { class: 'col g4' });

  function refresh(): void {
    summary.textContent = describe(recurrence);
    rruleText.textContent = toRRule(recurrence);

    const issues = validateRecurrence(recurrence);
    problems.replaceChildren(
      ...issues.map((message) => h('span', { class: 'errtx', text: message })),
    );
    render();
  }

  /// A tervek `.rd` rádiógombja címkével. A valódi input megmarad — csak nem
  /// látszik —, hogy a billentyűzet és a képernyőolvasó ugyanúgy kezelje.
  function radioRow(
    name: string,
    label: string,
    on: boolean,
    onSelect: () => void,
    ...extra: (HTMLElement | null)[]
  ): HTMLElement {
    return h(
      'label',
      {
        class: 'row g8',
        style: { alignItems: 'center', cursor: 'pointer', position: 'relative' },
      },
      h('input', {
        type: 'radio',
        name,
        checked: on ? 'checked' : undefined,
        style: { position: 'absolute', opacity: '0', width: '20px', height: '20px', margin: '0' },
        onChange: onSelect,
      }),
      h('span', { class: `rd${on ? ' on' : ''}`, 'aria-hidden': 'true' }),
      h('span', { class: 'body-sm', text: label }),
      ...extra,
    );
  }

  /// „Ismétlés minden N hónapban" — a terv külön mezőt ad rá.
  ///
  /// A szabályban az `interval` eddig is szerepelt, de MEGADNI sehol nem
  /// lehetett: minden ismétlődés kötelezően „minden 1." időszakra szólt.
  function intervalRow(): HTMLElement {
    const unit: Record<Frequency, string> = {
      daily: t('recur.unitDaily'),
      weekly: t('recur.unitWeekly'),
      monthly: t('recur.unitMonthly'),
      yearly: t('recur.unitYearly'),
    };

    return h(
      'div',
      { class: 'row g10', style: { alignItems: 'center' } },
      h('input', {
        class: 'input mono',
        type: 'number',
        min: '1',
        max: '99',
        value: String(recurrence.interval),
        'aria-label': t('recur.freqLabel'),
        style: { width: '84px', textAlign: 'center' },
        onChange: (event: Event) => {
          const value = Number((event.target as HTMLInputElement).value);
          recurrence.interval = Number.isFinite(value) && value >= 1 ? Math.floor(value) : 1;
          refresh();
        },
      }),
      h('span', { class: 'body-sm', text: unit[recurrence.frequency] }),
    );
  }

  function frequencyRow(): HTMLElement {
    const labels: Record<Frequency, string> = {
      daily: t('recur.freqDaily'),
      weekly: t('recur.freqWeekly'),
      monthly: t('recur.freqMonthly'),
      yearly: t('recur.freqYearly'),
    };
    return h(
      'div',
      { class: 'seg' },
      ...(Object.keys(labels) as Frequency[]).map((frequency) =>
        h('button', {
          type: 'button',
          class: recurrence.frequency === frequency ? 'on' : '',
          'aria-pressed': recurrence.frequency === frequency ? 'true' : 'false',
          text: labels[frequency],
          onClick: () => {
            recurrence.frequency = frequency;
            if (frequency === 'weekly' && !recurrence.weekdays?.length) {
              recurrence.weekdays = ['MO'];
            }
            if (frequency === 'monthly' && !recurrence.monthly) {
              recurrence.monthly = { kind: 'dayOfMonth', day: 1 };
            }
            refresh();
          },
        }),
      ),
    );
  }

  function weekdayRow(): HTMLElement {
    const selected = new Set(recurrence.weekdays ?? []);
    return h(
      'div',
      { class: 'row g6 wrap' },
      ...weekdayOrder.map((day) =>
        h('button', {
          type: 'button',
          class: `chip${selected.has(day) ? ' on' : ''}`,
          'aria-pressed': selected.has(day) ? 'true' : 'false',
          text: weekdayName(day),
          onClick: () => {
            selected.has(day) ? selected.delete(day) : selected.add(day);
            recurrence.weekdays = [...selected];
            refresh();
          },
        }),
      ),
    );
  }

  function monthlyRow(): HTMLElement {
    const mode = recurrence.monthly ?? { kind: 'dayOfMonth' as const, day: 1 };
    const dayInput = h('input', {
      class: 'input',
      type: 'number',
      min: '1',
      max: '31',
      value: String(mode.kind === 'dayOfMonth' ? mode.day : 1),
      style: { width: '96px' },
      'aria-label': t('recur.dayOfMonthLabel'),
      onChange: (event: Event) => {
        recurrence.monthly = {
          kind: 'dayOfMonth',
          day: Number((event.target as HTMLInputElement).value),
        };
        refresh();
      },
    });

    return h(
      'div',
      { class: 'col g6' },
      h(
        'div',
        { class: 'row g12 wrap', style: { alignItems: 'center' } },
        radioRow('monthly-mode', t('recur.dayOfMonthRadio'), mode.kind === 'dayOfMonth', () => {
          recurrence.monthly = { kind: 'dayOfMonth', day: 1 };
          refresh();
        }),
        radioRow(
          'monthly-mode',
          t('recur.weekdayOfMonthRadio'),
          mode.kind === 'weekdayOfMonth',
          () => {
          recurrence.monthly = { kind: 'weekdayOfMonth', ordinal: 1, weekday: 'MO' };
          refresh();
        }),
      ),
      mode.kind === 'dayOfMonth'
        ? dayInput
        : h(
            'div',
            { class: 'row g6 wrap' },
            ...([1, 2, 3, 4, -1] as number[]).map((ordinal) =>
              h('button', {
                type: 'button',
                class: `chip${mode.ordinal === ordinal ? ' on' : ''}`,
                text: t(
                  {
                    1: 'recur.ordinal1',
                    2: 'recur.ordinal2',
                    3: 'recur.ordinal3',
                    4: 'recur.ordinal4',
                    '-1': 'recur.ordinalLast',
                  }[String(ordinal) as '1' | '2' | '3' | '4' | '-1'],
                ),
                onClick: () => {
                  recurrence.monthly = { kind: 'weekdayOfMonth', ordinal, weekday: mode.weekday };
                  refresh();
                },
              }),
            ),
            ...weekdayOrder.map((day) =>
              h('button', {
                type: 'button',
                class: `chip${mode.weekday === day ? ' on' : ''}`,
                text: weekdayName(day),
                onClick: () => {
                  recurrence.monthly = {
                    kind: 'weekdayOfMonth',
                    ordinal: mode.ordinal,
                    weekday: day,
                  };
                  refresh();
                },
              }),
            ),
          ),
    );
  }

  function endRow(): HTMLElement {
    const kinds: { key: Recurrence['end']['kind']; label: string }[] = [
      { key: 'never', label: t('recur.endNever') },
      { key: 'until', label: t('recur.endUntil') },
      { key: 'count', label: t('recur.endCount') },
    ];

    return h(
      'div',
      { class: 'col g8' },
      ...kinds.map((k) =>
        radioRow(`end-kind`, k.label, recurrence.end.kind === k.key, () => {
          recurrence.end =
            k.key === 'never'
              ? { kind: 'never' }
              : k.key === 'count'
                ? { kind: 'count', count: 12 }
                : { kind: 'until', date: new Date(Date.now() + 365 * 86400000) };
          refresh();
        }),
      ),
      recurrence.end.kind === 'until'
        ? h('input', {
            class: 'input',
            type: 'date',
            'aria-label': t('recur.endLabel'),
            value: recurrence.end.date.toISOString().slice(0, 10),
            onChange: (event: Event) => {
              recurrence.end = {
                kind: 'until',
                date: new Date((event.target as HTMLInputElement).value),
              };
              refresh();
            },
          })
        : recurrence.end.kind === 'count'
          ? h('input', {
              class: 'input',
              type: 'number',
              min: '1',
              'aria-label': t('recur.countLabel'),
              value: String(recurrence.end.count),
              style: { width: '120px' },
              onChange: (event: Event) => {
                recurrence.end = {
                  kind: 'count',
                  count: Number((event.target as HTMLInputElement).value),
                };
                refresh();
              },
            })
          : h('span'),
    );
  }

  function field(label: string, ...children: (HTMLElement | null)[]): HTMLElement {
    return h('div', { class: 'field' }, h('span', { class: 'flbl', text: label }), ...children);
  }

  function render(): void {
    // A terv az osszefoglalo alatt a KOVETKEZO alkalmakat is kiirja: ez az
    // egyetlen visszajelzes arrol, hogy a szabaly tenyleg azt jelenti, amit a
    // felhasznalo gondol.
    const upcoming = nextOccurrences(recurrence, new Date(), 12);
    const shown = upcoming.slice(0, 4);
    const rest =
      recurrence.end.kind === 'count'
        ? Math.max(0, recurrence.end.count - shown.length)
        : Math.max(0, upcoming.length - shown.length);

    body.replaceChildren(
      field(t('recur.frequency'), frequencyRow()),
      field(t('recur.every'), intervalRow()),
      recurrence.frequency === 'weekly' ? field(t('recur.days'), weekdayRow()) : h('span'),
      recurrence.frequency === 'monthly'
        ? field(t('recur.monthlyWhich'), monthlyRow())
        : h('span'),
      field(t('recur.end'), endRow()),
      h(
        'div',
        { class: 'sunken pad16 col g6' },
        h('span', { class: 'label', text: t('task.recurrenceSummary') }),
        summary,
        rruleText,
        shown.length > 0
          ? h(
              'div',
              { class: 'row g6 wrap', style: { marginTop: '4px' } },
              ...shown.map((date) =>
                h('span', {
                  class: 'chip',
                  style: { height: '26px' },
                  text: `${String(date.getMonth() + 1).padStart(2, '0')}. ${String(
                    date.getDate(),
                  ).padStart(2, '0')}.`,
                }),
              ),
              rest > 0
                ? h('span', { class: 'chip', style: { height: '26px' }, text: t('recur.moreCount', [String(rest)]) })
                : null,
            )
          : h('span', { class: 'caption', text: t('recur.noOccurrence') }),
      ),
      problems,
    );
  }

  refresh();

  openDialog({
    title: t('recur.title'),
    width: '560px',
    body: () =>
      h(
        'div',
        { class: 'col g16' },
        h('span', { class: 'caption', text: options.projectTitle }),
        body,
      ),
    footer: (close) => [
      h('span', { class: 'grow' }),
      h('button', { class: 'btn btn-secondary', type: 'button', text: t('general.cancel'), onClick: close }),
      h('button', {
        class: 'btn btn-primary',
        type: 'button',
        text: t('general.save'),
        onClick: () => {
          // A figyelmeztetés (pl. 31. nap) nem blokkol, a valódi hiba igen.
          const issues = validateRecurrence(recurrence);
          const blocking = issues.filter((m) => !m.includes('kimaradhat'));
          if (blocking.length > 0) {
            refresh();
            return;
          }
          options.onSubmit({ recurrence, rrule: toRRule(recurrence) });
          close();
        },
      }),
    ],
  });
}

// ---------------------------------------------------------------------------
// W22 — Sablonszerkesztő
// ---------------------------------------------------------------------------

/// W22 — Sablonszerkesztő.
///
/// A terv kétoszlopos: balra a sablonok listája, jobbra a kiválasztott sablon
/// szerkesztője. Korábban egyetlen sablont lehetett szerkeszteni, a többit
/// meg se lehetett nézni.
export function openTemplateEditor(options: {
  /// A cég sablonjai. Üres listánál egy új sablon szerkesztője nyílik.
  templates?: ProjectTemplate[];
  /// Melyik sablon legyen kiválasztva induláskor (pl. „Sablonként mentés").
  template?: ProjectTemplate;
  jobTitles: string[];
  onSubmit: (template: { id?: string; name: string; tasks: TemplateTask[] }) => void;
}): void {
  const library = options.templates ?? [];

  let current: ProjectTemplate =
    options.template ?? library[0] ?? { id: '', name: '', tasks: [] };
  let tasks: TemplateTask[] = current.tasks.map((t) => ({ ...t }));

  const name = textField({
    label: 'Sablon neve',
    required: true,
    value: current.name,
    maxLength: 100,
  });

  /// A terv „Alapértelmezett prioritás" mezője: az újonnan felvett
  /// feladatok ezzel indulnak.
  const defaultPriority = selectField({
    label: t('task.defaultPriority'),
    value: 'mid',
    choices: (Object.keys(priorityMeta) as TaskPriority[]).map((p) => ({
      value: p,
      label: priorityLabel(p),
    })),
  });

  const listHost = h('div');
  const libraryHost = h('div', { class: 'panel', style: { overflow: 'hidden' } });
  const headerHost = h('div', { class: 'col', style: { gap: '2px' } });

  function select(template: ProjectTemplate): void {
    current = template;
    tasks = template.tasks.map((t) => ({ ...t }));
    name.input.value = template.name;
    render();
  }

  function renderLibrary(): void {
    libraryHost.replaceChildren(
      h(
        'div',
        {
          class: 'row between',
          style: { padding: '12px 14px', borderBottom: '1px solid var(--border)', alignItems: 'center' },
        },
        h('span', { class: 'label', text: 'Sablonok' }),
        h(
          'button',
          {
            class: 'btn btn-ghost btn-icon btn-sm',
            type: 'button',
            'aria-label': t('task.newType'),
            onClick: () => select({ id: '', name: '', tasks: [] }),
          },
          h('span', { class: 'ic', style: { width: '15px', height: '15px' } }, icon(icons.plus, 15)),
        ),
      ),
      ...(library.length === 0
        ? [
            h('span', {
              class: 'caption',
              style: { padding: '12px 14px', display: 'block' },
              text: t('task.noSavedTemplate'),
            }),
          ]
        : library.map((template) =>
            h(
              'button',
              {
                type: 'button',
                class: `navitem${template.id === current.id ? ' on' : ''}`,
                style: {
                  borderRadius: '0',
                  height: '46px',
                  width: '100%',
                  border: '0',
                  font: 'inherit',
                  cursor: 'pointer',
                  textAlign: 'left',
                },
                onClick: () => select(template),
              },
              h(
                'span',
                { class: 'col', style: { gap: '0', minWidth: '0', alignItems: 'flex-start' } },
                h('span', { class: 'body-sm w6', text: template.name }),
                h('span', { class: 'caption', text: `${template.tasks.length} feladat` }),
              ),
            ),
          )),
    );
  }

  function render(): void {
    renderLibrary();

    headerHost.replaceChildren(
      h('span', { class: 'h3', text: name.input.value.trim() || t('task.newType') }),
      h('span', { class: 'caption', text: `${tasks.length} feladat` }),
    );

    listHost.replaceChildren(
      tasks.length === 0
        ? h('span', { class: 'body-sm tm', text: t('task.noTaskInTemplate') })
        : reorderableList(tasks, render, (index) => {
            openTemplateTaskDialog({
              task: tasks[index],
              jobTitles: options.jobTitles,
              onSubmit: (updated) => {
                tasks[index] = updated;
                render();
              },
            });
          }),
    );
  }

  name.input.addEventListener('input', () => {
    headerHost.replaceChildren(
      h('span', { class: 'h3', text: name.input.value.trim() || t('task.newType') }),
      h('span', { class: 'caption', text: `${tasks.length} feladat` }),
    );
  });

  render();

  openDialog({
    title: 'Projektsablonok',
    width: '980px',
    body: () =>
      h(
        'div',
        { class: 'row', style: { gap: '20px', alignItems: 'flex-start', flexWrap: 'wrap' } },
        h('div', { class: 'col g12', style: { width: '280px', flex: 'none' } }, libraryHost),
        h(
          'div',
          { class: 'col g14', style: { flex: '1 1 0', minWidth: '320px', gap: '14px' } },
          headerHost,
          h('div', { class: 'grid c2' }, name.element, defaultPriority.element),
          listHost,
          h(
            'div',
            { class: 'row g8 wrap' },
            h(
              'button',
              {
                class: 'btn btn-secondary',
                type: 'button',
                onClick: () =>
                  openTemplateTaskDialog({
                    jobTitles: options.jobTitles,
                    onSubmit: (created) => {
                      tasks.push({
                        ...created,
                        priority: created.priority ?? (defaultPriority.input.value as TaskPriority),
                      });
                      render();
                    },
                  }),
              },
              h('span', { class: 'ic', style: { width: '14px', height: '14px' } }, icon(icons.plus, 14)),
              h('span', { text: t('task.addTaskToTemplate') }),
            ),
          ),
        ),
      ),
    footer: (close) => [
      h('span', { class: 'grow' }),
      h('button', { class: 'btn btn-secondary', type: 'button', text: t('general.cancel'), onClick: close }),
      h('button', {
        class: 'btn btn-primary',
        type: 'button',
        text: t('task.saveTemplate'),
        onClick: () => {
          if (!validateAll([name] as FieldControl<HTMLElement>[])) return;
          options.onSubmit({
            id: current.id || undefined,
            name: name.input.value.trim(),
            tasks,
          });
          close();
        },
      }),
    ],
  });
}
